import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as AlertsIcon } from '@elementary/common-ui/assets/20x20/alerts.svg';
import { ReactComponent as MetadataIcon } from '@elementary/common-ui/assets/20x20/configuration.svg';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/24x24/info.svg';
import {
  Flex,
  FlexColumn,
  Breadcrumbs,
  PageAlert,
  InternalLink,
  Tabs,
  Tab,
  GenerateAIButton,
} from '@elementary/common-ui/components';
import { useBreadcrumbs } from '@elementary/common-ui/hooks/useBreadcrumbs';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { AlertProps } from '@mui/material';
import { useLocation } from 'react-router-dom';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useHasDbtProjects from '@elementary/common-ui/hooks/useHasDbtProjects';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import AddTestMenuButton from 'shared/components/AddTestMenuButton';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { useCodeRepositoryIntegration } from 'shared/hooks/integrations';
import { TestManagementTabs } from 'testManagement/types';
import { space } from '@elementary/common-ui/style/utils';
import TestManagementTable from './TestManagementTable';

const Wrapper = styled(FlexColumn)`
  height: 100%;
`;

const Header = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled(Flex)`
  align-items: center;
  gap: ${space('xxl')};
`;

const TabsContainer = styled(Tabs)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.subtle};
  margin: ${space('sm', 'xxl', 'xl', 'xxl')};
`;

const PageAlertWithTransition = styled(PageAlert)<
  AlertProps & { $visible: boolean }
>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  height: ${({ $visible }) => ($visible ? '48px' : '0')};
  transition: all 0.2s ease-in-out;
`;

const TestManagementPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigateWithEnv();
  const [searchParams] = useSearchParams();
  const selectedEnvId = useSelectedEnv();

  const currentTab: TestManagementTabs = useMemo(
    () => (pathname.split('/').pop() === 'alerts' ? 'alerts' : 'configuration'),
    [pathname],
  );

  const [isCodeRepoAlertVisible, setIsCodeRepoAlertVisible] = useState(false);

  const { data: account } = useAccount();

  const breadcrumbs = useBreadcrumbs({
    pageTitle: 'Test Configuration',
  });

  const hasDbtProjects = useHasDbtProjects();
  const { shouldConnectCodeRepo, isRepositoryConnected } =
    useCodeRepositoryIntegration({
      envId: selectedEnvId,
    });

  useEffect(() => {
    setIsCodeRepoAlertVisible(!isRepositoryConnected && hasDbtProjects);
  }, [isRepositoryConnected, hasDbtProjects]);

  const setCurrentTab = (val: TestManagementTabs) => {
    const newParams = new URLSearchParams(searchParams);
    navigate(`/test-management/${val.toLowerCase()}?${newParams.toString()}`, {
      replace: true,
    });
  };

  const tabs = useMemo(
    () => [
      {
        value: 'configuration',
        label: 'Configuration',
        unselectedIcon: <MetadataIcon />,
        selectedIcon: <MetadataIcon />,
      },
      {
        value: 'alerts',
        label: 'Alert Destinations',
        unselectedIcon: <AlertsIcon width={20} height={20} />,
        selectedIcon: <AlertsIcon width={20} height={20} />,
      },
    ],
    [],
  );

  return (
    <Wrapper>
      <FlexColumn>
        <PageAlertWithTransition
          $visible={isCodeRepoAlertVisible}
          icon={<InfoIcon />}
          onClose={() => setIsCodeRepoAlertVisible(false)}
        >
          <InternalLink to="/settings/environments">
            Connect your code repository
          </InternalLink>{' '}
          to unlock all test configuration options.
        </PageAlertWithTransition>
        <Header sx={{ paddingInline: 2, pt: 3, pb: 1 }}>
          <HeaderLeft>
            <Breadcrumbs items={breadcrumbs} />
          </HeaderLeft>
          <Flex sx={{ gap: space('sm') }}>
            {account?.ai_features_enabled && (
              <GenerateAIButton>Manage tests with AI</GenerateAIButton>
            )}
            <AddTestMenuButton
              onChange={(item) => navigate(`/test-management/add-test/${item}`)}
            />
          </Flex>
        </Header>

        <TabsContainer
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
        >
          {tabs.map(({ value, label, selectedIcon, unselectedIcon }) => (
            <Tab
              key={value}
              $size="small"
              value={value}
              iconPosition="start"
              icon={currentTab === value ? selectedIcon : unselectedIcon}
              label={label}
            />
          ))}
        </TabsContainer>
      </FlexColumn>

      <TestManagementTable
        urlParamsPrefix="test_management_tables"
        shouldConnectCodeRepo={shouldConnectCodeRepo}
        mode={currentTab}
      />
    </Wrapper>
  );
};

export default TestManagementPage;
